import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TrackingConfig {
  gtm_container_id: string | null;
  meta_pixel_id: string | null;
  meta_event_code: string | null;
}

interface CodeSnippet {
  id: string;
  snippet_type: string;
  code: string;
}

interface CookiePreferences {
  functional: boolean;
  analytical: boolean;
  marketing: boolean;
}

const COOKIE_PREFERENCES_KEY = "cookie-preferences";
const LEADINFO_ID = "LI-6972BAB94B9E8";

/**
 * Reads consent from localStorage. Returns null if user has not yet decided.
 * Marketing scripts (GTM, Meta Pixel, Leadinfo) require `marketing: true`.
 *
 * NOTE: ProfitMetrics CSS-selector for email-capture is configured in the PM dashboard as:
 *       [data-track$="-email"]
 * This suffix-match restricts capture to legitimate visitor-identity inputs
 * (login, register, forgot-password, checkout, newsletter) and excludes admin CRUD forms.
 */
function readConsent(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookiePreferences;
  } catch {
    return null;
  }
}

/**
 * Injects GTM, Meta Pixel, Leadinfo, and custom code snippets — gated by consent.
 * All marketing scripts wait for `marketing: true` in localStorage cookie-preferences.
 */
export const TrackingScripts = () => {
  const [config, setConfig] = useState<TrackingConfig | null>(null);
  const [snippets, setSnippets] = useState<CodeSnippet[]>([]);
  const [consent, setConsent] = useState<CookiePreferences | null>(() => readConsent());

  // Listen for consent changes dispatched by CookieConsent.tsx
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CookiePreferences>).detail;
      if (detail) setConsent(detail);
      else setConsent(readConsent());
    };
    window.addEventListener("cookie-consent-updated", handler);
    return () => window.removeEventListener("cookie-consent-updated", handler);
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data: integrationData, error: integrationError } = await supabase
          .from("analytics_integrations")
          .select("integration_key, is_enabled, config")
          .in("integration_key", ["google_tag_manager", "meta_pixel"]);

        if (integrationError) {
          console.error("Failed to fetch tracking config:", integrationError);
        }

        let snippetData: CodeSnippet[] = [];
        try {
          const snippetResponse = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-public-snippets`,
            { method: "GET", headers: { "Content-Type": "application/json" } }
          );
          if (snippetResponse.ok) {
            const snippetResult = await snippetResponse.json();
            snippetData = snippetResult.snippets || [];
          }
        } catch (snippetError) {
          console.error("Failed to fetch code snippets:", snippetError);
        }

        const gtm = integrationData?.find((d) => d.integration_key === "google_tag_manager");
        const pixel = integrationData?.find((d) => d.integration_key === "meta_pixel");

        const gtmConfig = gtm?.config as { container_id?: string } | null;
        const pixelConfig = pixel?.config as { pixel_id?: string; event_code?: string } | null;

        setConfig({
          gtm_container_id: gtm?.is_enabled && gtmConfig?.container_id ? gtmConfig.container_id : null,
          meta_pixel_id: pixel?.is_enabled && pixelConfig?.pixel_id ? pixelConfig.pixel_id : null,
          meta_event_code: pixelConfig?.event_code || null,
        });

        setSnippets(snippetData || []);
      } catch (err) {
        console.error("Tracking config error:", err);
      }
    };

    fetchConfig();
  }, []);

  // ProfitMetrics email-capture: MutationObserver scans for [data-track$="-email"]
  // and dispatches `pm:identity` events when those inputs change. Runs immediately
  // (no consent gate — first-party identity capture is functional).
  useEffect(() => {
    const SELECTOR = '[data-track$="-email"]';
    const seen = new WeakSet<HTMLInputElement>();

    const attach = (input: HTMLInputElement) => {
      if (seen.has(input)) return;
      seen.add(input);
      const handleChange = () => {
        const email = input.value.trim().toLowerCase();
        if (!email || !email.includes("@")) return;
        const context = input.getAttribute("data-track") || "unknown-email";
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
          event: "pm_identity",
          pm_email: email,
          pm_context: context,
        });
      };
      input.addEventListener("change", handleChange);
      input.addEventListener("blur", handleChange);
    };

    document.querySelectorAll<HTMLInputElement>(SELECTOR).forEach(attach);

    const observer = new MutationObserver((mutations) => {
      for (const mut of mutations) {
        mut.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.(SELECTOR)) attach(node as HTMLInputElement);
          node.querySelectorAll?.<HTMLInputElement>(SELECTOR).forEach(attach);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // Marketing scripts: GTM + Meta Pixel — only inject when marketing consent granted.
  useEffect(() => {
    if (!config) return;
    if (!consent?.marketing) return;

    const injectTracking = () => {
      if (config.gtm_container_id) injectGTM(config.gtm_container_id);
      if (config.meta_pixel_id) injectMetaPixel(config.meta_pixel_id, config.meta_event_code);
      injectLeadinfo(LEADINFO_ID);
    };

    // Reduced from 5s to 1s — LCP is typically <1.5s with preload, so ProfitMetrics
    // and other tracking can capture early form interactions without race conditions.
    const timeoutId = setTimeout(() => {
      if ("requestIdleCallback" in window) {
        (window as any).requestIdleCallback(injectTracking, { timeout: 2000 });
      } else {
        injectTracking();
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [config, consent]);

  // Snippets (header/footer custom code, often contains tracking) — also gated.
  useEffect(() => {
    if (snippets.length === 0) return;
    if (!consent?.marketing) return;

    const timeoutId = setTimeout(() => {
      const headerSnippets = snippets.filter((s) => s.snippet_type === "header");
      headerSnippets.forEach((snippet) => injectSnippet(snippet.id, snippet.code, "header"));
      const footerSnippets = snippets.filter((s) => s.snippet_type === "footer");
      footerSnippets.forEach((snippet) => injectSnippet(snippet.id, snippet.code, "footer"));
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [snippets, consent]);

  return null;
};

// Note: window.dataLayer typing is provided by gtag.js types in the project's global scope.
// We use a runtime-only assignment via `(window as any).dataLayer` style is unnecessary because
// CookieConsent.tsx augments Window already; we just push.


function injectGTM(containerId: string) {
  if (!/^GTM-[A-Z0-9]{1,20}$/i.test(containerId)) return;
  if (document.getElementById("gtm-script")) return;

  const script = document.createElement("script");
  script.id = "gtm-script";
  script.async = true;
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `;
  document.head.appendChild(script);

  const noscript = document.createElement("noscript");
  noscript.id = "gtm-noscript";
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

/**
 * Extra Meta Pixel events are stored as text; only plain fbq('track'|'trackCustom', 'Name')
 * calls are kept, so stored settings can never run arbitrary scripts on visitors' browsers.
 */
function safeMetaEventCalls(eventCode: string | null): string {
  if (!eventCode) return "";
  const re = /fbq\(\s*['"](track|trackCustom)['"]\s*,\s*['"]([A-Za-z0-9_]{1,50})['"]\s*\)/g;
  const calls: string[] = [];
  for (const m of eventCode.matchAll(re)) {
    if (calls.length >= 10) break;
    calls.push(`fbq('${m[1]}', '${m[2]}');`);
  }
  return calls.join("\n");
}

function injectMetaPixel(pixelId: string, eventCode: string | null) {
  if (!/^\d{5,20}$/.test(pixelId)) return;
  if (document.getElementById("meta-pixel-script")) return;

  const script = document.createElement("script");
  script.id = "meta-pixel-script";
  script.async = true;
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
    ${safeMetaEventCalls(eventCode)}
  `;
  document.head.appendChild(script);

  const noscript = document.createElement("noscript");
  noscript.id = "meta-pixel-noscript";
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.head.appendChild(noscript);
}

/**
 * Leadinfo — moved from index.html so injection is consent-gated (marketing).
 */
function injectLeadinfo(leadinfoId: string) {
  if (document.getElementById("leadinfo-script")) return;

  const script = document.createElement("script");
  script.id = "leadinfo-script";
  script.async = true;
  script.innerHTML = `
    (function(l,e,a,d,i,n,f,o){if(!l[i]){l.GlobalLeadinfoNamespace=l.GlobalLeadinfoNamespace||[];
    l.GlobalLeadinfoNamespace.push(i);l[i]=function(){(l[i].q=l[i].q||[]).push(arguments)};l[i].t=l[i].t||n;
    l[i].q=l[i].q||[];o=e.createElement(a);f=e.getElementsByTagName(a)[0];o.async=1;o.src=d;f.parentNode.insertBefore(o,f);}
    }(window,document,'script','https://cdn.leadinfo.net/ping.js','leadinfo','${leadinfoId}'));
  `;
  document.head.appendChild(script);
}

function injectSnippet(id: string, code: string, type: "header" | "footer") {
  const elementId = `snippet-${id}`;
  if (document.getElementById(elementId)) return;
  if (!code.trim()) return;

  const container = document.createElement("div");
  container.id = elementId;
  container.innerHTML = code;

  const target = type === "header" ? document.head : document.body;

  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === "SCRIPT") {
      const oldScript = node as HTMLScriptElement;
      const script = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });
      if (oldScript.textContent) script.textContent = oldScript.textContent;
      target.appendChild(script);
    } else {
      target.appendChild(node.cloneNode(true));
    }
  });
}

export default TrackingScripts;
